import { Box, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import AppButton from "@/common/components/Button/AppButton";
import Datetime from "@/common/components/Datetime/Datetime";
import FoldableBlock from "@/common/components/Display/FoldableBlock";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import { PdfViewerModal } from "@/common/components/Files/PDF/PdfViewerModal";
import BooleanValue from "@/common/components/Form/Display/BooleanValue";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import AppIcon from "@/common/components/Icons/AppIcon";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { ApiEnumName } from "@/common/services/enum";
import {
  CustomerAcknowledgementDto,
  CustomerAcknowledgementStatus,
  PersonalDataDeleteDto,
} from "@/core/api/generated/v1";

import { PersonalDataDeleteModal } from "../../Common/PersonalData/PersonalDataDeleteModal";
import { PersonalDataDeletionDisplay } from "../../Common/PersonalData/PersonalDataDeletionDisplay";
import GeneralByWhoDisplay from "../../General/Display/GeneralByWhoDisplay";
import CustomerContactCard from "../CustomerContactCard";

export interface CustomerAcknowledgementDisplayProps {
  value: CustomerAcknowledgementDto;
  onDeletePersonalData: (dto: PersonalDataDeleteDto) => Promise<void>;
}

export function CustomerAcknowledgementDisplay({
  value,
  onDeletePersonalData,
}: CustomerAcknowledgementDisplayProps) {
  const canDeletePersonalData =
    value.status !== CustomerAcknowledgementStatus.PersonalDataDeleted && !!onDeletePersonalData;

  const [fileItemPdfToViewInModalOpen, setFileItemPdfToViewInModalOpen] = useState<FileItem | null>(
    null,
  );
  const [isDeletePersonalDataModalOpen, setIsDeletePersonalDataModalOpen] = useState(false);

  const vehicleStatePdfFileItem = useMemo(
    () =>
      value.vehicleActualStatePdf ? FileItem.createFrom(value.vehicleActualStatePdf) : undefined,
    [value.vehicleActualStatePdf],
  );
  const signatureImageFileItem = useMemo(
    () => (value.signatureImage ? FileItem.createFrom(value.signatureImage) : undefined),
    [value.signatureImage],
  );
  const personalDocumentImageFileItems = useMemo(
    () =>
      value.personalDocumentImages
        ? FileItem.createManyFrom([...value.personalDocumentImages])
        : undefined,
    [value.personalDocumentImages],
  );

  return (
    <Stack gap={1}>
      <Stack gap={0.5}>
        <FieldValue
          variant='filled'
          direction='column'
          label='Status'
          isEmpty={!value.status}
          endAdornment={
            canDeletePersonalData && (
              <AppButton
                size='small'
                color='text'
                startIcon={<AppIcon of='delete' />}
                onClick={() => setIsDeletePersonalDataModalOpen(true)}
              >
                Delete personal data
              </AppButton>
            )
          }
        >
          <InlineApiEnumValue
            type={ApiEnumName.CustomerAcknowledgementStatus}
            value={value.status}
            withHelperTooltip
          />
        </FieldValue>

        <FieldValue
          variant='filled'
          direction='column'
          label='Captured'
          isEmpty={!value.capturedAt}
        >
          <Stack direction='row' gap={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            {value.capturedBy && <GeneralByWhoDisplay who={{ userId: value.capturedBy }} />}{" "}
            {value.capturedAt && <Datetime datetime={value.capturedAt} withDurationFromNow />}{" "}
          </Stack>
        </FieldValue>

        <FieldValue
          variant='filled'
          direction='column'
          label='Privacy policy accepted'
          isEmpty={!value.privacyPolicyAcceptedAt}
        >
          <BooleanValue value={value.isPrivacyPolicyAccepted} />
          {value.privacyPolicyAcceptedAt && (
            <>
              , <Datetime datetime={value.privacyPolicyAcceptedAt} withDurationFromNow />
            </>
          )}
        </FieldValue>

        <FieldValue variant='filled' direction='column' label='Sent to customer'>
          <BooleanValue value={value.isSentToCustomer} />
          {value.sentToCustomerAt && (
            <>
              , <Datetime datetime={value.sentToCustomerAt} withDurationFromNow />
            </>
          )}
        </FieldValue>

        {value.contact && (
          <FieldValue variant='filled' direction='column' label='Customer contact'>
            <CustomerContactCard contact={value.contact} />
          </FieldValue>
        )}
      </Stack>

      {(value.signedAt || value.signatureImage) && (
        <Stack gap={1}>
          <FieldValue variant='filled' direction='column' label='Signature'>
            <Stack gap={0.5}>
              {value.signatureImage && (
                <FileListView
                  files={signatureImageFileItem ? [signatureImageFileItem] : []}
                  maxVisibleFiles={1}
                  itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
                  itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
                  fileItemViewProps={{ sx: { background: "white" } }}
                />
              )}

              <Datetime datetime={value.signedAt} withDurationFromNow />
            </Stack>
          </FieldValue>
        </Stack>
      )}

      {!ArrayHelper.isEmpty(value.personalDocumentImages) && (
        <FieldValue variant='filled' direction='column' label='Documents'>
          <FileListView
            files={personalDocumentImageFileItems || []}
            maxVisibleFiles={10}
            itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
            itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
          />
        </FieldValue>
      )}

      {value.vehicleActualStatePdf && (
        <FieldValue variant='filled' direction='column' label='Vehicle state PDF'>
          <FileListView
            files={vehicleStatePdfFileItem ? [vehicleStatePdfFileItem] : []}
            maxVisibleFiles={1}
            itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
            itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
            fileItemViewProps={{
              actions: {
                enabled: true,
                quickView: true,
                onClick: (fileItem) => {
                  setFileItemPdfToViewInModalOpen(fileItem);
                },
                onQuickView: (fileItem) => {
                  setFileItemPdfToViewInModalOpen(fileItem);
                },
              },
            }}
          />
        </FieldValue>
      )}

      {fileItemPdfToViewInModalOpen && (
        <PdfViewerModal
          viewerProps={{ fileItem: fileItemPdfToViewInModalOpen, scale: 1.4 }}
          modalTitle={<Box component='span'>Vehicle state PDF</Box>}
          open={!!fileItemPdfToViewInModalOpen}
          onClose={() => {
            setFileItemPdfToViewInModalOpen(null);
          }}
        />
      )}

      <FoldableBlock
        defaultIsFolded
        trigger={{
          label: <Typography variant='h6'>More</Typography>,
        }}
      >
        <Stack gap={0.5}>
          <FieldValue variant='filled' direction='column' label='Type' isEmpty={!value.type}>
            <InlineApiEnumValue
              type={ApiEnumName.CustomerAcknowledgementType}
              value={value.type}
              withHelperTooltip
            />
          </FieldValue>

          <FieldValue variant='filled' direction='column' label='Method' isEmpty={!value.method}>
            <InlineApiEnumValue
              type={ApiEnumName.CustomerVerificationMethod}
              value={value.method}
              withHelperTooltip
            />
          </FieldValue>

          <FieldValue
            variant='filled'
            direction='column'
            label='Acknowledgement short text'
            isEmpty={!value.ackShortText}
          >
            {value.ackShortText}
          </FieldValue>

          <FieldValue
            variant='filled'
            direction='column'
            label='Damage book viewed at'
            isEmpty={!value.damageBookViewedAt}
          >
            <Datetime datetime={value.damageBookViewedAt} withDurationFromNow />
          </FieldValue>
        </Stack>
      </FoldableBlock>

      {value.status === CustomerAcknowledgementStatus.PersonalDataDeleted &&
        value.personalDataDeletion && (
          <FoldableBlock
            defaultIsFolded
            trigger={{
              label: <Typography variant='h6'>Personal data deletion info</Typography>,
            }}
          >
            <PersonalDataDeletionDisplay value={value.personalDataDeletion} />
          </FoldableBlock>
        )}

      {canDeletePersonalData && onDeletePersonalData && (
        <PersonalDataDeleteModal
          open={isDeletePersonalDataModalOpen}
          onClose={() => {
            setIsDeletePersonalDataModalOpen(false);
          }}
          subProps={{
            onCancel: () => {
              setIsDeletePersonalDataModalOpen(false);
            },
            onSave: async (dto) => {
              await onDeletePersonalData(dto);
              setIsDeletePersonalDataModalOpen(false);
            },
          }}
          modelTitleProps={{
            title: `Delete customer personal data`,
          }}
        />
      )}
    </Stack>
  );
}
